import React from 'react';
import ManagedTable, { ManagedColumn, ManagedColumnExtra, ManagedTableProps } from 'app/common/table/ManagedTable';
import { OpLogEntry, OpLogEntryType } from 'app/model/CORE';
import TimeUtils from 'app/common/util/TimeUtils';
import Utils from 'app/common/util/Utils';
import useWindowDimensions from 'app/common/hooks/useWindowDimensions';

export class OpLogEntryColumn extends ManagedColumn<OpLogEntry> {
    constructor(
        header: string,
        width: string,
        getCellValue: (object: OpLogEntry) => any,
        extra?: ManagedColumnExtra<OpLogEntry>,
    ) {
        super(header, width, getCellValue, extra);
    }
}

export class StandardOpLogEntryColumn {
    public static readonly TIME = new OpLogEntryColumn('TIME', '8em', obj => TimeUtils.formatDDHHMMSS(obj.timestamp));
    public static readonly TYPE = new OpLogEntryColumn('TYPE', '5em', obj => toOplogTypeString(obj));
    public static readonly ISSUER = new OpLogEntryColumn('ISSUER', '15em', obj => obj.issuer);
    public static readonly SUMMARY = new OpLogEntryColumn('SUMMARY', '15em', obj =>
        Utils.concat(' ', obj.summaryFields),
    );
    public static readonly IFPL_ID = new OpLogEntryColumn('IFPL ID', '10em', obj => obj.ifplId);
    public static readonly ETFMS_ID = new OpLogEntryColumn('ETFMS ID', '10em', obj => obj.etfmsId);
}

export const STANDARD_OP_LOG_ENTRY_COLUMN_LIST = [
    StandardOpLogEntryColumn.TIME,
    StandardOpLogEntryColumn.TYPE,
    StandardOpLogEntryColumn.ISSUER,
    StandardOpLogEntryColumn.SUMMARY,
    StandardOpLogEntryColumn.IFPL_ID,
    StandardOpLogEntryColumn.ETFMS_ID,
];

function toOpLogTypeShortString(entry: OpLogEntry) {
    if (entry) {
        switch (entry.type) {
            case OpLogEntryType.ENVIRONMENT_MESSAGE:
                return 'EN';
            case OpLogEntryType.ERRONEOUS_INCOMING_MESSAGE:
                return 'ER';
            case OpLogEntryType.ERROR_MESSAGE:
                return 'EM';
            case OpLogEntryType.HISTORY:
                return 'HI';
            case OpLogEntryType.INCOMING_MESSAGE:
                return 'IM';
            case OpLogEntryType.OUTGOING_MESSAGE:
                return 'OM';
            case OpLogEntryType.PROCESS_ERROR:
                return 'PE';
            case OpLogEntryType.TEXT_MESSAGE:
                return 'TM';
            case OpLogEntryType.UNDEFINED:
                return 'UD';
            case OpLogEntryType.USER_COMMAND:
                return 'UC';
            case OpLogEntryType.VIOLATION:
                return 'VI';
            case OpLogEntryType.WARNING:
                return 'WA';
        }
        return '??'; // should never happen
    }
}

function toOplogTypeString(entry: OpLogEntry) {
    if (entry) {
        const code = toOpLogTypeShortString(entry);
        if (entry.type === OpLogEntryType.INCOMING_MESSAGE || entry.type === OpLogEntryType.OUTGOING_MESSAGE) {
            const idx = entry.message.indexOf('-TITLE '); // so, expect no spaces between hyphen and 'TITLE'
            if (idx !== -1) {
                const spcIdx = entry.message.indexOf(' ', idx + 7);
                const hyphenIdx = entry.message.indexOf('-', idx + 7);
                const endIdx = Math.min(hyphenIdx, spcIdx);
                if (endIdx !== -1) {
                    return code + ' ' + entry.message.substring(idx + 7, endIdx).trim();
                }
            }
        }
        return code;
    }
}

class OpLogEntryTableBase extends ManagedTable<OpLogEntry> {}

// I have had to adapt ManagedTable so that it accepts a font size.
// VirtualTable that is behind only accepts column width in "px", whereas our column definition is in "em".
// This usually is not a problem when font size is fixed, but in our case, font size depends on the viewport width.
// When the viewport is resized, the new font size is provided to the flight table, that will adapt column width accordingly.
const OpLogEntryTable = (props: ManagedTableProps<OpLogEntry>) => {
    const { width } = useWindowDimensions();
    const fontSize = (0.42 * width) / 100;

    return <OpLogEntryTableBase fontSize={fontSize} {...props} />;
};

export default OpLogEntryTable;
